// Phase 8 — landing hub for the goStops Procurement Module.
//
// One live tool (Amazon Invoice Downloader) + one placeholder tile.
// Deliberately minimal on stats/emojis — we don't fake numbers to
// look busy; each new tool ships with real metrics or none at all.

import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

function ToolTile({ to, title, description, statusLabel, statusVariant, testid, disabled }) {
  const body = (
    <Card
      className={
        "h-full transition-colors border-border/70 " +
        (disabled
          ? "bg-muted/20 border-dashed cursor-default"
          : "hover:border-foreground/40 hover:shadow-sm")
      }
      data-testid={testid}
    >
      <CardHeader className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">{title}</CardTitle>
          {statusLabel && (
            <Badge
              variant={statusVariant || "secondary"}
              className="uppercase tracking-wider text-[10px]"
              data-testid={`${testid}-status`}
            >
              {statusLabel}
            </Badge>
          )}
        </div>
        <CardDescription className="text-xs leading-relaxed">
          {description}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {!disabled && (
          <span className="text-xs text-primary underline underline-offset-4">
            Open tool →
          </span>
        )}
        {disabled && (
          <span className="text-xs text-muted-foreground italic">
            More tools coming soon.
          </span>
        )}
      </CardContent>
    </Card>
  );
  if (disabled) return body;
  return (
    <Link to={to} className="block h-full">
      {body}
    </Link>
  );
}

export default function Landing() {
  return (
    <div className="space-y-8" data-testid="landing-page">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight" data-testid="landing-heading">
          Welcome to the goStops Procurement Module
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Your procurement &amp; analytics workspace — pick a tool below to get started.
        </p>
      </header>

      <section
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="landing-tile-grid"
      >
        <ToolTile
          to="/tools/amazon-invoices"
          title="Amazon Invoice Downloader"
          description="Bulk-download GST invoices + credit notes from your Amazon Business account with a filterable date range."
          statusLabel="Active"
          statusVariant="default"
          testid="tile-amazon-invoices"
        />
        <ToolTile
          to="/tools/fnb-po"
          title="F&B Stock & PO Release"
          description="Track F&B inventory across all locations, analyze consumption trends, and generate purchase orders in both Pan-India and location-wise formats."
          statusLabel="Active"
          statusVariant="default"
          testid="tile-fnb-po"
        />
        <ToolTile
          disabled
          title="Add new tool →"
          description="Vendor reconciliation, SAP export bridge, and more procurement automations are on the roadmap."
          testid="tile-placeholder"
        />
      </section>

      <footer className="pt-6 text-xs text-muted-foreground border-t border-border/60">
        goStops Procurement Module · v0.8
      </footer>
    </div>
  );
}
